import React, {ReactElement, useEffect, ReactNode, HTMLAttributes} from 'react';
import styles from './row-collapse.module.scss'
import classNames from 'classnames';
import { CollapseButton } from '../../../collapse-button';

interface GridDebugWindow extends Window {
    __TIMESHEET_OVERLAY_DEBUG__?: boolean;
}

const GRID_DEBUG_STORAGE_KEY = 'timesheet.overlay.debug';

const isGridDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const debugWindow = window as GridDebugWindow;
    if (typeof debugWindow.__TIMESHEET_OVERLAY_DEBUG__ === 'boolean') {
        return debugWindow.__TIMESHEET_OVERLAY_DEBUG__;
    }

    try {
        const persisted = window.localStorage.getItem(GRID_DEBUG_STORAGE_KEY);
        if (persisted !== null) {
            return persisted === '1';
        }
    } catch {
        // no-op
    }

    return process.env.NODE_ENV !== 'production';
};

const gridDebugLog = (event: string, payload?: unknown) => {
    if (!isGridDebugEnabled()) {
        return;
    }

    if (payload === undefined) {
        console.debug('[TimesheetGridGroup]', event);
        return;
    }

    console.debug('[TimesheetGridGroup]', event, payload);
};

const GROUP_OPEN_CHANGE_EVENT = 'timesheet-group-open-change';

export interface State {
    rowKey: any;
    open: boolean;
    isAbleToOpen: boolean;
    forceCollapse: () => void;
    forceExpand: () => void;
}

interface Props {
    rowKey: any;
    rowHeader: ReactElement;
    children: ReactNode[];
    onStateChange: (state: State) => void;
    nested: number;
    contentProps?: React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    showButton?: boolean | null;
    initialOpen?: boolean;
}

export const RowCollapse: React.FC<Props> = (props) => {
    const { rowKey: key, rowHeader, children, contentProps, showButton } = props;
    const initialState = !!(props.initialOpen && showButton);
    const [open, setOpen] = React.useState(initialState);
    const prevRowKeyRef = React.useRef<any>(key);
    const prevOpenRef = React.useRef<boolean>(initialState);
    const hasAppliedInitialRef = React.useRef(initialState);
    const onStateChangeRef = React.useRef(props.onStateChange);
    onStateChangeRef.current = props.onStateChange;

    const handleButtonClick = () => {
        setOpen((currentOpen) => !currentOpen);
    }

    useEffect(() => {
        if (prevRowKeyRef.current !== key) {
            prevRowKeyRef.current = key;
            const nextInitialState = !!(props.initialOpen && showButton);
            hasAppliedInitialRef.current = nextInitialState;
            setOpen(nextInitialState);
        }
    }, [key, props.initialOpen, showButton]);

    useEffect(() => {
        if (!!showButton && props.initialOpen && !hasAppliedInitialRef.current) {
            hasAppliedInitialRef.current = true;
            setOpen(true);
        }
    }, [props.initialOpen, showButton]);

    useEffect(() => {
        onStateChangeRef.current({
            rowKey: key,
            open,
            forceCollapse: () => setOpen(false),
            forceExpand: () => setOpen(true),
            isAbleToOpen: !!showButton,
        });

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(GROUP_OPEN_CHANGE_EVENT, {
                detail: {
                    key: String(key),
                    level: props.nested,
                    open,
                    showButton: !!showButton,
                }
            }));
        }

        if (prevOpenRef.current !== open || prevRowKeyRef.current !== key) {
            gridDebugLog('group:open-state-changed', {
                key: String(key),
                level: props.nested,
                open,
                showButton: !!showButton,
            });
            prevOpenRef.current = open;
        }
    }, [key, open, showButton]);

    const rowKey = String(key);
    const isGroupOpen = open && !!showButton;

    return(<div
        key={key}
        data-grid-group-root={'true'}
        data-grid-group-key={rowKey}
        data-grid-group-level={props.nested}
        data-group-open={isGroupOpen ? 'true' : 'false'}
    >
        <div className={classNames({
            [styles.collapseHeader]: true,
            [styles.nested]: props.nested > 0
        })}
        data-grid-group-header={'true'}
        data-grid-group-key={rowKey}
        data-grid-group-level={props.nested}
        >
            {showButton && (
                <div
                    className={styles.collapseButtonWrapper}
                    data-grid-group-toggle={'true'}
                    data-grid-group-key={rowKey}
                    data-grid-group-level={props.nested}
                >
                    <CollapseButton
                        isExpanded={open}
                        onExpandClick={() => handleButtonClick()}
                    />
                </div>
            )}
            <div className={styles.collapseHeaderContent}>
                {rowHeader}
            </div>
        </div> 
        {
            open && showButton && <div className={classNames({
                [styles.collapseContent]: true,
                [styles.open]: open,
            })} {...contentProps} >
                {
                    children.map((c) => (c))
                }
            </div>
        }
    </div>)
}
